import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppFileUploader, AppRemixIcon } from '@/layout/AppComponents';
import { Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../../components/helpers/Translator';
import EditorTranslatable from '../../../../components/form/Texts/EditorTranslatable';
import { axiosPath } from '../../../../../routing/helpers/router';
import errorHandler from '@/layout/components/error/ErrorHandler';
import InputTranslatable from '@/layout/components/form/Inputs/InputTranslatable';
import { toFormData } from '../../../../../minions/forms';
import minions from '../../../../../minions/minions';
import colors from '../../../../../constants/colors';

export default function InternalObligationForm({
  internalObligationTemplateId,
  internalObligation,
  onReload,
}) {
  const dispatch = useDispatch();
  const { moduleBridgeHex, plantHex } = useParams();
  const [validationSchema] = useState(
    Yup.object().shape({
      obligation: Yup.mixed()
        .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
      file: Yup.mixed().test(
        'file',
        'Debes rellenar la referencia legal',
        function(value) {
          if (value?.length > 0) {
            return Object.keys(this.parent?.law).length > 0;
          }
          return true;
        }
      ),
    })
  );

  const handleSave = useCallback(
    (payload) =>
      axios
        .post(
          axiosPath(
            `api_r_user_module_legalCompliance_internalObligation_${
              internalObligationTemplateId ? "add" : "edit"
            }`
          ),
          toFormData({
            ...payload,
            moduleBridgeHex,
            plantHex,
            internalObligationTemplateId,
            internalObligationHex: internalObligation?.hex,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => onReload())
        .catch((e) => dispatch(errorHandler(e, true))),
    [internalObligation]
  );

  const handleUpload = useCallback((setFieldValue, opt) => {
    setFieldValue('deletedFile', true);
    setFieldValue('file', [...opt]);
  }, []);

  return (
    <Formik
      initialValues={{
        obligation: internalObligation?.edit?.obligation || {},
        evidence: internalObligation?.edit?.evidence || {},
        nextDate: internalObligation?.edit?.nextDate || {},
        law: internalObligation?.edit?.law || {},
        file: [],
        deletedFile: false,
      }}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values) => handleSave(values)}
    >
      {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
        <>
          <div className="row mb-2">
            <div className="col-lg-12">
              <EditorTranslatable
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-legalCompliance"
                    name="obligation-text"
                  />
                )}
                icon={<AppRemixIcon icon="file-text" />}
                onChange={(opt) => setFieldValue('obligation', opt)}
                value={values.obligation}
                error={errors.obligation}
                className=""
                toolbar={{
                  toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    ['clean'],
                    [{ direction: 'rtl' }],
                  ],
                }}
                height={200}
              />
              {errors?.obligation && (
                <div className="text-danger">*{errors?.obligation}</div>
              )}
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-lg-6">
              <EditorTranslatable
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-legalCompliance"
                    name="text-evidence"
                  />
                )}
                icon={<AppRemixIcon icon="file-text" />}
                onChange={(opt) => setFieldValue('evidence', opt)}
                value={values.evidence}
                error={errors.evidence}
                className=""
                toolbar={{
                  toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    ['clean'],
                    [{ direction: 'rtl' }],
                  ],
                }}
                height={200}
              />
            </div>
            <div className="col-lg-6">
              <EditorTranslatable
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-legalCompliance"
                    name="periodicity-text"
                  />
                )}
                icon={<AppRemixIcon icon="file-text" />}
                onChange={(opt) => setFieldValue('nextDate', opt)}
                value={values.nextDate}
                error={errors.nextDate}
                className=""
                toolbar={{
                  toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    ['clean'],
                    [{ direction: 'rtl' }],
                  ],
                }}
                height={200}
              />
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-lg-12">
              <h6 className="mt-2">
                <Translator
                  file="module-legalCompliance"
                  name="module.lc.legal-reference"
                />
              </h6>
              <InputTranslatable
                icon={<AppRemixIcon icon="auction" />}
                values={values.law}
                onChange={(value) => setFieldValue('law', value)}
                placeholder={ReactDOMServer.renderToString(
                  <Translator
                    file="module-legalCompliance"
                    name="module.lc.legal-reference"
                  />
                )}
                error={errors?.file}
              />
              {errors?.file && (
                <div className="text-danger">*{errors?.file}</div>
              )}
              <AppFileUploader
                placeholder={ReactDOMServer.renderToString(
                  <Translator
                    file="general"
                    name={
                      values?.file?.length > 0
                        ? 'one.file.selected'
                        : 'select.a.file'
                    }
                  />
                )}
                onChange={(opt) => handleUpload(setFieldValue, opt)}
                btnText={<Translator file="general" name="search..." />}
              />
              {internalObligation?.edit?.lawFile && !values?.deletedFile && (
                <FileList uploaded>
                  <li>
                    <AppRemixIcon
                      className="pointer mr-3"
                      icon="delete-bin"
                      color={colors.danger}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="click.delete"
                        />
                      )}
                      onClick={() => setFieldValue('deletedFile', true)}
                    />
                    {minions.files.fileIcon(
                      internalObligation?.edit?.lawFile,
                      true,
                      null,
                      'mr-2'
                    )}
                    <span>{internalObligation?.edit?.lawFile}</span>
                  </li>
                </FileList>
              )}
              <FileList>
                {values?.file?.map((f, i) => (
                  <li key={i}>
                    <AppRemixIcon
                      className="pointer mr-3"
                      icon="delete-bin"
                      color={colors.danger}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="click.delete"
                        />
                      )}
                      onClick={() => setFieldValue('file', [])}
                    />
                    {minions.files.fileIcon(f?.name, true, null, 'mr-2')}
                    <span>{f?.name}</span>
                  </li>
                ))}
              </FileList>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <button
                type="submit"
                className="btn btn-success btn-block"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                <Translator
                  file="module-legalCompliance"
                  name={
                    internalObligationTemplateId
                      ? 'create-obligation'
                      : 'edit-obligation'
                  }
                />
              </button>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
}

const FileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${(props) => (props.uploaded ? colors.grey200 : 'initial')};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
